import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';

// Import components
import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { EnableScheduleCard } from '@/components/features/schedule/enable-schedule-card';
import { NoSchedulesCard } from '@/components/features/schedule/no-schedules-card';
import { ScheduleEnabledBadge } from '@/components/features/schedule/schedule-enabled-badge';
import { SchedulePastille } from '@/components/features/schedule/schedule-pastille';
import { ScheduleSelector } from '@/components/features/schedule/schedule-selector';
import { Button } from '@/components/ui/button';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderAction,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { capitalize } from '@/lib/utils';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CalendarIntegration } from './-parts/calendar-integration-card';
import { CurrentWeekCard } from './-parts/current-week-card';
import { NextOnCallCard } from './-parts/next-on-call-card';
import { TimelineCard } from './-parts/timeline-card';
import { useSelectedSchedule } from './-parts/use-selected-schedule';

export const Route = createFileRoute('/_authenticated/dashboard/')({
  component: DashboardPage,
});

function DashboardPage() {
  const organization = useCurrentOrganization();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    selectedSchedule,
    schedules,
    onScheduleChange,
    isFetched,
    selectedScheduleMembers: members,
  } = useSelectedSchedule(organization.organizationId);

  if (isFetched && schedules.all.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh)]">
        <NoSchedulesCard
          onCreateSchedule={() => {
            navigate({ to: '/schedule/create' });
          }}
        />
      </div>
    );
  }

  if (isFetched && schedules.enabled.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh)]">
        <EnableScheduleCard
          onEnableSchedule={() => {
            navigate({ to: '/schedule' });
          }}
        />
      </div>
    );
  }

  if (!selectedSchedule) {
    return null;
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <SchedulePastille
                color={selectedSchedule.color}
                size="lg"
                variant="line"
              />
              <PageHeaderTitle>
                {capitalize(selectedSchedule.name)}
              </PageHeaderTitle>
            </div>
            <ScheduleEnabledBadge schedule={selectedSchedule} />
          </div>
          <PageHeaderDescription>
            {t('authenticated.dashboard.description')}
          </PageHeaderDescription>
        </PageHeaderInfo>

        <PageHeaderAction>
          <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-4 w-full">
            {selectedSchedule && (
              <Link
                to="/schedule/$scheduleId"
                params={{ scheduleId: selectedSchedule.scheduleId }}
                className="w-full sm:w-auto shrink-0 flex"
              >
                <Button
                  variant="outline"
                  className="w-full gap-2 justify-center"
                  size="default"
                  icon={<ExternalLink />}
                >
                  <span className="xs:inline">
                    {t('authenticated.dashboard.viewSchedule')}
                  </span>
                </Button>
              </Link>
            )}

            <div className="w-full sm:flex-1">
              <ScheduleSelector
                schedules={schedules.enabled}
                selectedSchedule={selectedSchedule}
                onScheduleChange={onScheduleChange}
              />
            </div>
          </div>
        </PageHeaderAction>
      </PageHeader>

      <PageContent>
        {/* On-Call Cards */}
        <div className="flex gap-4 flex-col md:flex-row w-full mt-6">
          <div className="flex-1">
            <CurrentWeekCard
              organizationId={organization.organizationId}
              schedule={selectedSchedule}
              members={members}
            />
          </div>
          <div className="flex-1">
            <NextOnCallCard
              organizationId={organization.organizationId}
              schedule={selectedSchedule}
              members={members}
            />
          </div>
        </div>

        {/* Current Month (timeline) */}
        <div className="mt-6">
          <TimelineCard
            organizationId={organization.organizationId}
            schedule={selectedSchedule}
          />
        </div>

        {/* Calendar Block */}
        <div className="mt-4">
          <CalendarIntegration selectedSchedule={selectedSchedule} />
        </div>
      </PageContent>
    </Page>
  );
}
